import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useMountGuideMount } from '../../hooks/useMountGuideData';
import type { MountGuideSkill } from '../../types';
import { guideText } from '../../utils/guideLocale';
import { resolvePublicMediaUrl } from '../../utils/newsContent';
import { parseMountDetails } from '../../utils/mountGuideDetails';
import '../../styles/MountGuide.css';

const MountGuideDetail: React.FC = () => {
    const { regionSlug, mountSlug } = useParams<{ regionSlug: string; mountSlug: string }>();
    const { locale } = useLanguage();
    const { data: mount, loading } = useMountGuideMount(mountSlug);

    const isPt = locale === 'pt-BR';
    const showLoading = loading && !mount;

    if (showLoading) {
        return <p className="game-sidebar-muted">{isPt ? 'Carregando…' : 'Loading…'}</p>;
    }

    if (!mount) {
        return (
            <div className="game-main-inner">
                <p>{isPt ? 'Montaria não encontrada.' : 'Mount not found.'}</p>
                <Link to="/game/montarias" className="btn btn-outline btn-sm">
                    {isPt ? 'Voltar' : 'Back'}
                </Link>
            </div>
        );
    }

    const title = guideText(locale, mount.titleEn, mount.titlePtBr);
    const regionTitle = guideText(locale, mount.region.titleEn, mount.region.titlePtBr);
    const rarityTitle = guideText(locale, mount.rarity.titleEn, mount.rarity.titlePtBr);
    const description = guideText(locale, mount.descriptionEn, mount.descriptionPtBr);
    const hero = resolvePublicMediaUrl(mount.heroImageUrl) || resolvePublicMediaUrl(mount.iconUrl);
    const details = parseMountDetails(mount.detailsJson);
    const mapUrl = resolvePublicMediaUrl(details.mapImageUrl);
    const captureConditions = isPt
        ? details.captureConditions
        : details.captureConditionsEn ?? details.captureConditions;

    const statLabel = (row: { labelEn?: string; labelPtBr?: string; key: string }) =>
        isPt ? row.labelPtBr || row.key : row.labelEn || row.key;

    const formatSkillTiming = (raw?: string) => {
        if (!raw) return '';
        if (isPt) {
            if (raw === 'Instant') return 'Instantâneo';
            return raw.replace(/(\d+)\s*s\b/g, '$1 s').replace(/(\d+)\s*min\b/g, '$1 min');
        }
        return raw;
    };

    const skillTypeLabel = (raw?: string) => {
        if (!raw) return '';
        const mapPt: Record<string, string> = {
            passive: 'Passiva',
            Passive: 'Passiva',
            Buff: 'Buff',
            Attack: 'Ataque',
            バフ: 'Buff',
            攻撃: 'Ataque',
        };
        const mapEn: Record<string, string> = {
            passive: 'Passive',
            Passive: 'Passive',
            Buff: 'Buff',
            Attack: 'Attack',
            バフ: 'Buff',
            攻撃: 'Attack',
        };
        const map = isPt ? mapPt : mapEn;
        return map[raw] ?? raw;
    };

    const renderSkillCard = (skill: MountGuideSkill, i: number) => {
        const skillName = (isPt ? skill.namePtBr : skill.nameEn)?.trim();
        const effect = (isPt ? skill.effectPtBr : skill.effectEn)?.trim();
        if (!skillName && !effect) return null;
        const skillIcon = resolvePublicMediaUrl(skill.iconUrl);
        return (
            <div key={i} className="mount-guide-skill-card">
                {skillIcon && (
                    <div className="mount-guide-skill-icon">
                        <img src={skillIcon} alt="" />
                    </div>
                )}
                <table className="mount-guide-info-table" style={{ flex: 1 }}>
                    <tbody>
                        {skill.skillType && (
                            <tr>
                                <th>{isPt ? 'Tipo' : 'Type'}</th>
                                <td>{skillTypeLabel(skill.skillType)}</td>
                            </tr>
                        )}
                        <tr>
                            <th>{isPt ? 'Habilidade' : 'Skill'}</th>
                            <td>{skillName}</td>
                        </tr>
                        {skill.maxLevel != null && (
                            <tr>
                                <th>{isPt ? 'Nível máx.' : 'Max Lv'}</th>
                                <td>{skill.maxLevel}</td>
                            </tr>
                        )}
                        {skill.energy && (
                            <tr>
                                <th>{isPt ? 'Energia' : 'Energy'}</th>
                                <td>{skill.energy}</td>
                            </tr>
                        )}
                        {skill.distance && (
                            <tr>
                                <th>{isPt ? 'Distância' : 'Distance'}</th>
                                <td>{skill.distance}</td>
                            </tr>
                        )}
                        {skill.cast && (
                            <tr>
                                <th>{isPt ? 'Conjuração' : 'Cast'}</th>
                                <td>{formatSkillTiming(skill.cast)}</td>
                            </tr>
                        )}
                        {skill.cooldown && (
                            <tr>
                                <th>{isPt ? 'Recarga' : 'Cooldown'}</th>
                                <td>{formatSkillTiming(skill.cooldown)}</td>
                            </tr>
                        )}
                        {skill.damage && (
                            <tr>
                                <th>{isPt ? 'Dano' : 'Damage'}</th>
                                <td>{skill.damage}</td>
                            </tr>
                        )}
                        {effect && (
                            <tr>
                                <th>{isPt ? 'Efeito' : 'Effect'}</th>
                                <td>{effect}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="game-main-inner mount-guide-page">
            <nav className="mount-guide-breadcrumb">
                <Link to="/game">{isPt ? 'Guia' : 'Guide'}</Link>
                <span>›</span>
                <Link to="/game/montarias">{isPt ? 'Índice' : 'Index'}</Link>
                <span>›</span>
                <Link to={`/game/montarias/${mount.region.slug}`}>{regionTitle}</Link>
                <span>›</span>
                <span>{title}</span>
            </nav>

            <h1 className="mount-guide-region-title">{title}</h1>
            <p style={{ color: 'var(--text-muted)', marginTop: '-0.5rem' }}>{rarityTitle}</p>

            {hero && <img className="mount-guide-detail-hero" src={hero} alt={title} />}

            {description && <p className="mount-guide-detail-desc">{description}</p>}

            <h2 className="mount-guide-section-heading">{isPt ? 'Informações básicas' : 'Basic information'}</h2>
            <table className="mount-guide-info-table mount-guide-info-table-grid">
                <tbody>
                    <tr>
                        <th>{isPt ? 'nome' : 'Name'}</th>
                        <td>{title}</td>
                        <th>{isPt ? 'Tipo móvel' : 'Movement type'}</th>
                        <td>{isPt ? details.moveTypePtBr : details.moveTypeEn}</td>
                    </tr>
                    {details.grade != null && details.capturePoints != null && (
                        <tr>
                            <th>{isPt ? 'nota' : 'Grade'}</th>
                            <td>{'★'.repeat(Math.min(5, Math.max(0, details.grade)))}</td>
                            <th>{isPt ? 'Pontos necessários para a captura' : 'Capture points'}</th>
                            <td>{details.capturePoints}</td>
                        </tr>
                    )}
                    {details.monsterLevel != null && captureConditions?.[0] && (
                        <tr>
                            <th>{isPt ? 'Nível Monstro' : 'Monster level'}</th>
                            <td>{details.monsterLevel}</td>
                            <th>{isPt ? 'Condição de Captura 1' : 'Capture condition 1'}</th>
                            <td>{captureConditions[0]}</td>
                        </tr>
                    )}
                    {(details.fellowLevelMin != null || details.fellowLevelMax != null) &&
                        captureConditions?.[1] && (
                            <tr>
                                <th>{isPt ? 'Nível de associado' : 'Associate level'}</th>
                                <td>
                                    {details.fellowLevelMin}-{details.fellowLevelMax}
                                </td>
                                <th>{isPt ? 'Condição de Captura 2' : 'Capture condition 2'}</th>
                                <td>{captureConditions[1]}</td>
                            </tr>
                        )}
                </tbody>
            </table>

            {(details.spawnAreaPtBr || details.spawnAreaEn) && (
                <>
                    <h2 className="mount-guide-section-heading">{isPt ? 'Área de aparição' : 'Spawn area'}</h2>
                    <table className="mount-guide-info-table mount-guide-info-table-grid">
                        <tbody>
                            <tr>
                                <th>{isPt ? 'Área de aparição' : 'Spawn area'}</th>
                                <td>{isPt ? details.spawnAreaPtBr : details.spawnAreaEn}</td>
                                <th>{isPt ? 'Altitude de aparição' : 'Spawn altitude'}</th>
                                <td>
                                    {(() => {
                                        const alt = isPt ? details.spawnAltitude : details.spawnAltitude;
                                        const behavior = isPt
                                            ? details.spawnBehaviorPtBr
                                            : details.spawnBehaviorEn;
                                        if (alt && alt !== '-') return alt;
                                        return behavior || '—';
                                    })()}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </>
            )}

            {mapUrl && (
                <div className="mount-guide-map-wrap">
                    <img src={mapUrl} alt={isPt ? 'Mapa' : 'Map'} />
                    {details.mapMarker && (
                        <span
                            className="mount-guide-map-marker"
                            style={{
                                left: `${(details.mapMarker.x ?? 0.5) * 100}%`,
                                top: `${(details.mapMarker.y ?? 0.5) * 100}%`,
                            }}
                        />
                    )}
                </div>
            )}

            {details.notes && details.notes.length > 0 && (
                <ul className="mount-guide-notes">
                    {details.notes.map((note, i) => (
                        <li
                            key={i}
                            className={note.variant === 'warning' ? 'mount-guide-note-warning' : 'mount-guide-note-info'}
                        >
                            {isPt ? note.textPtBr : note.textEn || note.textPtBr}
                        </li>
                    ))}
                </ul>
            )}

            {!mapUrl &&
                (details.spawnAreaPtBr || details.spawnAreaEn) &&
                (details.spawnBehaviorPtBr || details.mapMarker) && (
                    <p className="mount-guide-map-missing">
                        {isPt
                            ? 'Mapa de spawn ainda não cadastrado. No admin, abra «Área de aparição e mapa» e envie a imagem.'
                            : 'Spawn map not uploaded yet. In admin, open «Spawn area and map» and upload the image.'}
                    </p>
                )}

            {details.stats && details.stats.length > 0 && (
                <>
                    <h2 className="mount-guide-section-heading">{isPt ? 'Estatísticas do familiar' : 'Fellow stats'}</h2>
                    <table className="mount-guide-info-table">
                        <thead>
                            <tr>
                                <th />
                                <th>{isPt ? 'Nível 1' : 'Level 1'}</th>
                                <th>{isPt ? 'Nível máx.' : 'Max level'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {details.stats.map((row, i) => (
                                <tr key={`${row.key}-${i}`}>
                                    <th>{statLabel(row)}</th>
                                    <td>{row.lv1}</td>
                                    <td>{row.lvMax}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {(isPt ? details.statsNotePtBr : details.statsNoteEn) && (
                        <p className="mount-guide-stats-note">
                            {isPt ? details.statsNotePtBr : details.statsNoteEn}
                        </p>
                    )}
                </>
            )}

            {details.skills && details.skills.length > 0 && (
                <>
                    <h2 className="mount-guide-section-heading">{isPt ? 'Habilidades' : 'Skills'}</h2>
                    <p className="mount-guide-skill-group-label">
                        [{isPt ? 'Habilidades passivas' : 'Passive skills'}]
                    </p>
                    {details.skills.map((skill, i) => renderSkillCard(skill, i))}
                </>
            )}

            {details.activeSkills && details.activeSkills.length > 0 && (
                <>
                    <p className="mount-guide-skill-group-label">
                        [{isPt ? 'Habilidades ativas' : 'Active skills'}]
                    </p>
                    {details.activeSkills.map((skill, i) => renderSkillCard(skill, i))}
                </>
            )}

            {details.captureMaterials && details.captureMaterials.length > 0 && (
                <>
                    <h2 className="mount-guide-section-heading">
                        {isPt ? 'Materiais para captura' : 'Capture materials'}
                    </h2>
                    <table className="mount-guide-info-table">
                        <thead>
                            <tr>
                                <th>{isPt ? 'Item' : 'Item'}</th>
                                <th>{isPt ? 'Quantidade' : 'Qty'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {details.captureMaterials.map((m, i) => (
                                <tr key={i}>
                                    <td>{isPt ? m.namePtBr : m.nameEn || m.namePtBr}</td>
                                    <td>{m.quantity}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {details.missionHints && details.missionHints.length > 0 && (
                <>
                    <h2 className="mount-guide-section-heading">
                        {isPt ? 'Missões (como obter)' : 'Missions (how to obtain)'}
                    </h2>
                    {details.missionHints.map((m, i) => (
                        <div key={i} className="mount-guide-mission-block">
                            <strong>{m.materialPtBr}</strong>
                            <p>{m.missionPtBr}</p>
                            <p className="mount-guide-stats-note">{m.detailsPtBr}</p>
                        </div>
                    ))}
                </>
            )}

            {details.dropSources && details.dropSources.length > 0 && (
                <>
                    <h2 className="mount-guide-section-heading">
                        {isPt ? 'Monstros que dropam' : 'Drop sources'}
                    </h2>
                    <table className="mount-guide-info-table">
                        <thead>
                            <tr>
                                <th>{isPt ? 'Item' : 'Item'}</th>
                                <th>{isPt ? 'Monstro' : 'Monster'}</th>
                                <th>{isPt ? 'Nível' : 'Level'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {details.dropSources.map((d, i) => (
                                <tr key={i}>
                                    <td>{isPt ? d.itemPtBr : d.itemEn || d.itemPtBr}</td>
                                    <td>{isPt ? d.monsterPtBr : d.monsterEn || d.monsterPtBr}</td>
                                    <td>{d.level}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {details.sealStone && details.sealStone.length > 0 && (
                <>
                    <h2 className="mount-guide-section-heading">
                        {isPt ? 'Pedra de selamento' : 'Seal stone'}
                    </h2>
                    <table className="mount-guide-info-table">
                        <thead>
                            <tr>
                                <th />
                                <th>{isPt ? 'Nível 1' : 'Level 1'}</th>
                                <th>{isPt ? 'Nível máx.' : 'Max level'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {details.sealStone.map((row, i) => (
                                <tr key={`${row.key}-${i}`}>
                                    <th>{statLabel(row)}</th>
                                    <td>{row.lv1}</td>
                                    <td>{row.lvMax}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            <button type="button" className="mount-guide-top-btn" onClick={() => window.scrollTo({ top: 0 })}>
                TOP
            </button>

            <p>
                <Link
                    to={`/game/montarias/${regionSlug || mount.region.slug}`}
                    className="btn btn-outline btn-sm"
                >
                    <i className="ri-arrow-left-line" /> {regionTitle}
                </Link>
            </p>
        </div>
    );
};

export default MountGuideDetail;
